import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Mail, Lock, User, Loader2, Clock, UserPlus, Hash, Eye, EyeOff } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { APP_NAME, APP_TAGLINE, APP_TITLE_PREFIX, APP_TITLE_SUFFIX, BRAND_ACCENT, IS_MAGIC_TEAM } from '../lib/brand';

const PENDING_MODE_KEY = 'mtc_pending_mode';

const Login = () => {
  const navigate = useNavigate();
  const { login, register, isAuthenticated, user } = useAuth();
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', name: '', sponsor: '', codice_incaricato: '' });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingMode, setPendingMode] = useState(() => {
    try {
      return sessionStorage.getItem(PENDING_MODE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const setPendingModePersisted = (value) => {
    setPendingMode(value);
    try {
      if (value) sessionStorage.setItem(PENDING_MODE_KEY, '1');
      else sessionStorage.removeItem(PENDING_MODE_KEY);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (user?.status === 'pending') {
      setPendingModePersisted(true);
    } else if (user?.status === 'approved') {
      setPendingModePersisted(false);
    }
  }, [user?.status]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Watch for status change from pending to approved (polling via pollingStatus)
  useEffect(() => {
    if (pendingMode && user?.status === 'approved') {
      navigate('/');
    }
  }, [pendingMode, user?.status, navigate]);

  const formatApiError = (detail) => {
    if (detail == null) return "Si è verificato un errore. Riprova.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      navigate('/');
    } catch (err) {
      if (!err.response) {
        setError('Impossibile contattare il server. Controlla la connessione e riprova.');
      } else {
        setError(formatApiError(err.response?.data?.detail) || 'Errore durante il login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    const cleaned = {
      email: registerData.email.trim(),
      password: registerData.password.trim(),
      name: registerData.name.trim(),
      sponsor: registerData.sponsor.trim(),
      codice_incaricato: registerData.codice_incaricato.trim(),
    };
    if (!cleaned.email || !cleaned.password || !cleaned.name || !cleaned.sponsor || !cleaned.codice_incaricato) {
      setError('Compila tutti i campi obbligatori');
      return;
    }
    if (cleaned.codice_incaricato.length !== 9) {
      setError('Il codice incaricato deve contenere esattamente 9 caratteri');
      return;
    }
    setLoading(true);
    try {
      await register(cleaned.email, cleaned.password, cleaned.name, cleaned.sponsor, cleaned.codice_incaricato);
      setPendingModePersisted(true); // Show waiting screen + start polling
    } catch (err) {
      if (!err.response) {
        setError('Impossibile contattare il server. Controlla la connessione e riprova.');
      } else {
        setError(formatApiError(err.response?.data?.detail) || 'Errore durante la registrazione');
      }
    } finally {
      setLoading(false);
    }
  };

  // Pending approval waiting screen
  if (pendingMode) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-white">
        <div className="max-w-md w-full text-center animate-fade-in">
          <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-[#FEF3C7] flex items-center justify-center">
            <Clock className="w-12 h-12 text-[#D4AF37]" />
          </div>
          <h1 className="text-2xl font-medium text-zinc-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Registrazione ricevuta
          </h1>
          <p className="text-zinc-600 mb-6">
            Il tuo account è in attesa di approvazione da parte dell'amministratore.
            Riceverai accesso automaticamente appena sarà approvato.
          </p>
          <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37]" />
            <span>Controllo stato in corso...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
      <div className={`min-h-screen flex items-center justify-center p-4 relative ${IS_MAGIC_TEAM ? 'app-page-shell' : 'bg-white'}`}>
      <div className="w-full max-w-md animate-fade-in relative">
        <div className="text-center mb-8">
          <BrandLogo size={96} radius={16} className={`mx-auto mb-4${IS_MAGIC_TEAM ? '' : ' shadow-xl'}`} />
          <h1 className="text-3xl font-light tracking-tight text-zinc-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {IS_MAGIC_TEAM ? (
              <>
                <span className="text-gold-gradient font-medium">{APP_TITLE_PREFIX}</span> {APP_TITLE_SUFFIX}
              </>
            ) : (
              <>
                <span className="text-zinc-900 font-medium">{APP_TITLE_PREFIX}</span> {APP_TITLE_SUFFIX}
              </>
            )}
          </h1>
          <p className="text-zinc-500 text-sm">{APP_TAGLINE}</p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-xl">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-zinc-100">
              <TabsTrigger value="login" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white rounded-md">
                Accedi
              </TabsTrigger>
              <TabsTrigger value="register" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white rounded-md">
                Registrati
              </TabsTrigger>
            </TabsList>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-zinc-700">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input id="login-email" type="email" placeholder="email@esempio.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="pl-10 bg-white border-zinc-200 text-zinc-900"
                      required data-testid="login-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-zinc-700">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input id="login-password" type={showLoginPassword ? 'text' : 'password'} placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="pl-10 pr-10 bg-white border-zinc-200 text-zinc-900"
                      required data-testid="login-password-input" />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      aria-label={showLoginPassword ? 'Nascondi password' : 'Mostra password'}
                      data-testid="login-password-toggle"
                    >
                      {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit"
                  className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-white font-semibold rounded-full py-5"
                  disabled={loading} data-testid="login-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Accedi'}
                </Button>
                <div className="text-center">
                  <Link to="/forgot-password" className="text-sm font-medium" style={{ color: IS_MAGIC_TEAM ? BRAND_ACCENT : '#D4AF37' }} data-testid="forgot-password-link">
                    Password dimenticata?
                  </Link>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="register-name" className="text-zinc-700">Nome e Cognome</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input id="register-name" type="text" placeholder="Mario Rossi"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      className="pl-10 bg-white border-zinc-200 text-zinc-900"
                      required data-testid="register-name-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-email" className="text-zinc-700">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input id="register-email" type="email" placeholder="email@esempio.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      className="pl-10 bg-white border-zinc-200 text-zinc-900"
                      required data-testid="register-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-sponsor" className="text-zinc-700">Nome e Cognome del tuo SPONSOR</Label>
                  <div className="relative">
                    <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input id="register-sponsor" type="text" placeholder="Nome e cognome sponsor"
                      value={registerData.sponsor}
                      onChange={(e) => setRegisterData({ ...registerData, sponsor: e.target.value })}
                      className="pl-10 bg-white border-zinc-200 text-zinc-900"
                      required
                      data-testid="register-sponsor-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-codice" className="text-zinc-700">Codice incaricato</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input id="register-codice" type="text" placeholder="Il tuo codice incaricato"
                      value={registerData.codice_incaricato}
                      onChange={(e) => setRegisterData({ ...registerData, codice_incaricato: e.target.value })}
                      className="pl-10 bg-white border-zinc-200 text-zinc-900"
                      required
                      minLength={9}
                      maxLength={9}
                      data-testid="register-codice-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-password" className="text-zinc-700">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input id="register-password" type={showRegisterPassword ? 'text' : 'password'} placeholder="Minimo 6 caratteri"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      className="pl-10 pr-10 bg-white border-zinc-200 text-zinc-900"
                      required minLength={6} data-testid="register-password-input" />
                    <button
                      type="button"
                      onClick={() => setShowRegisterPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      aria-label={showRegisterPassword ? 'Nascondi password' : 'Mostra password'}
                      data-testid="register-password-toggle"
                    >
                      {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit"
                  className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-white font-semibold rounded-full py-5"
                  disabled={loading} data-testid="register-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Registrati'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <p className="text-center text-zinc-400 text-xs mt-6">
          Accedendo accetti i termini di servizio e la privacy policy
        </p>
      </div>
    </div>
  );
};

export default Login;
